import L from 'leaflet'

/**
 * Calculate padding values based on UI state
 * 
 * @param {boolean} isImmersiveMode - Whether immersive mode is active
 * @param {string} panelState - Panel state ('minimized', 'semi', 'expanded')
 * @param {number} leftPanelWidth - Width of left panel
 * @returns {object} Padding values
 */
export function calculateMapPadding(isImmersiveMode, panelState, leftPanelWidth = 0) {
  // Bottom padding accounts for bottom-sheet panel with different panel states
  const bottomPadding = isImmersiveMode
    ? (panelState === 'minimized' ? 80 : panelState === 'semi' ? 180 : 420)
    : 50

  const baseSidePadding = 50
  const leftPadding = leftPanelWidth > 0 ? leftPanelWidth : baseSidePadding

  return {
    topPadding: 50,
    leftPadding,
    rightPadding: baseSidePadding,
    bottomPadding
  }
}

/**
 * Create bounds from user location and radius
 * 
 * @param {object} userLocation - User location with latitude/longitude
 * @param {number} radiusKm - Radius in kilometers
 * @returns {L.LatLngBounds} Leaflet bounds object
 */
export function createBoundsFromRadius(userLocation, radiusKm) {
  const radiusInMeters = radiusKm * 1000
  const latRadius = radiusInMeters / 111320 // roughly meters per degree latitude
  const lngRadius = radiusInMeters / (111320 * Math.cos(userLocation.latitude * Math.PI / 180))

  return L.latLngBounds(
    [userLocation.latitude - latRadius, userLocation.longitude - lngRadius],
    [userLocation.latitude + latRadius, userLocation.longitude + lngRadius]
  )
}

/**
 * Create bounds from markers with optional user location
 * 
 * @param {Array} markers - Array of marker entities
 * @param {object} userLocation - Optional user location
 * @param {number} extraPaddingFactor - Extra padding factor (default 0.15 = 15%)
 * @returns {L.LatLngBounds|null} Leaflet bounds object or null if invalid
 */
export function createBoundsFromMarkers(markers, userLocation = null, extraPaddingFactor = 0.15) {
  if (!markers || markers.length === 0) return null

  const bounds = L.latLngBounds()

  // Add user location if provided
  if (userLocation) {
    bounds.extend([userLocation.latitude, userLocation.longitude])
  }

  // Add all markers
  markers.forEach(entity => {
    if (entity.currentLatitude && entity.currentLongitude) {
      bounds.extend([entity.currentLatitude, entity.currentLongitude])
    }
  })

  if (!bounds.isValid()) return null

  // Add extra padding to prevent markers from being at the edge
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  const latDiff = Math.abs(ne.lat - sw.lat) * extraPaddingFactor
  const lngDiff = Math.abs(ne.lng - sw.lng) * extraPaddingFactor

  bounds.extend([sw.lat - latDiff, sw.lng - lngDiff])
  bounds.extend([ne.lat + latDiff, ne.lng + lngDiff])

  return bounds
}

/**
 * Fit map to bounds with padding
 * 
 * @param {L.Map} map - Leaflet map instance
 * @param {L.LatLngBounds} bounds - Bounds to fit
 * @param {object} padding - Padding values
 * @param {number} maxZoom - Maximum zoom level
 * @param {number} minZoom - Minimum zoom level (floor — never zoom out further than this)
 */
export function fitMapToBounds(map, bounds, padding, maxZoom = 15, minZoom = 12) {
  if (!map || !bounds || !bounds.isValid()) return

  map.fitBounds(bounds, {
    paddingTopLeft: [padding.leftPadding, padding.topPadding],
    paddingBottomRight: [padding.rightPadding, padding.bottomPadding],
    maxZoom,
    animate: false
  })

  // Leaflet has no built-in minZoom for fitBounds — clamp manually.
  // Prevents wide marker spreads (e.g. seed data across a whole state) from
  // zooming the map out to an unhelpful overview zoom like 9.
  if (map.getZoom() < minZoom) {
    map.setZoom(minZoom, { animate: false })
  }
}

/**
 * Calculate distance between two points in kilometers
 * 
 * @param {object} point1 - First point {lat, lng}
 * @param {object} point2 - Second point {lat, lng}
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(point1, point2) {
  const R = 6371 // Earth's radius in km
  const dLat = (point2.lat - point1.lat) * Math.PI / 180
  const dLon = (point2.lng - point1.lng) * Math.PI / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(point1.lat * Math.PI / 180) *
      Math.cos(point2.lat * Math.PI / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}
